import React, { useRef } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { toast } from 'react-toastify';
import { host, UploadFeatureImageUrl } from '../../proxyUrl';
import { useSelector } from 'react-redux';

// Store alt text temporarily for image insertion
let pendingAltText = null;
// Store nofollow flag temporarily for link insertion
let pendingNofollow = null;

// Custom upload adapter class
class CustomUploadAdapter {
  constructor(loader, host, token) {
    this.loader = loader;
    this.host = host;
    this.token = token;
  }

  upload() {
    return this.loader.file.then((file) => {
      return new Promise((resolve, reject) => {
        console.log('Uploading image:', file.name);
        
        // Prompt for alt text (same as ReactQuill version)
        const altText = prompt('Enter alt text for the image (optional):');
        pendingAltText = altText || null;

        const formData = new FormData();
        // Backend expects field name to be 'image', otherwise returns 'unexpected field' error
        formData.append('image', file);

        const xhr = new XMLHttpRequest();
        xhr.open('POST', UploadFeatureImageUrl, true);
        xhr.withCredentials = true;

        if (this.token) {
          xhr.setRequestHeader('Authorization', this.token);
        }

        xhr.onload = () => {
          if (xhr.status === 200 || xhr.status === 201) {
            try {
              const response = JSON.parse(xhr.responseText);
              if (response.fileUrl || response.imageUrl) {
                const imageUrl = response.fileUrl || response.imageUrl;
                console.log('Image uploaded successfully:', imageUrl);
                // Return object with default property containing the URL
                resolve({
                  default: imageUrl
                });
              } else {
                console.error('Upload failed: No fileUrl in response', response);
                toast.error('Error uploading image');
                reject(new Error('Upload failed: No fileUrl in response'));
              }
            } catch (error) {
              console.error('Error parsing upload response:', error);
              toast.error('Error uploading image');
              reject(new Error('Upload failed: Invalid response'));
            }
          } else {
            console.error('Upload failed:', xhr.status, xhr.statusText);
            toast.error('Error uploading image');
            reject(new Error(`Upload failed: ${xhr.statusText}`));
          }
        };

        xhr.onerror = () => {
          reject(new Error('Upload failed'));
          toast.error('Error uploading image');
        };

        xhr.send(formData);
      });
    });
  }

  abort() {
    // Handle abort if needed
  }
}

export default function CKEditorComponent({
  value,
  onChange,
}) {
  const editorRef = useRef(null);
  const user = useSelector((state) => state?.user?.user?.user);
  const storedToken = user?.token ? `Bearer ${user.token}` : null;

  return (
    <CKEditor
      editor={ClassicEditor}
      data={value || ''}
      onReady={(editor) => {
        editorRef.current = editor;

        // Configure upload adapter
        editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
          return new CustomUploadAdapter(loader, host, storedToken);
        };

        // Configure link schema to allow rel attribute
        const schema = editor.model.schema;
        
        // Extend link element to allow rel attribute
        if (schema.isRegistered('link')) {
          schema.extend('link', {
            allowAttributes: ['rel'],
          });
        }
        
        // Ensure imageBlock allows alt attribute (should be default, but ensure it)
        if (schema.isRegistered('imageBlock')) {
          schema.extend('imageBlock', {
            allowAttributes: ['alt'],
          });
        }
        
        // Configure conversion to preserve rel attribute in HTML output
        const conversion = editor.conversion;
        
        // Add attribute converter for rel attribute on links
        conversion.for('dataDowncast').attributeToAttribute({
          model: {
            name: 'link',
            key: 'rel',
          },
          view: {
            name: 'a',
            key: 'rel',
          },
        });
        
        // Also handle upcast (HTML to model) to preserve rel when loading content
        conversion.for('upcast').attributeToAttribute({
          view: {
            name: 'a',
            key: 'rel',
          },
          model: {
            name: 'link',
            key: 'rel',
          },
        });
        
        // Ensure alt attribute is preserved for images
        conversion.for('dataDowncast').attributeToAttribute({
          model: {
            name: 'imageBlock',
            key: 'alt',
          },
          view: {
            name: 'img',
            key: 'alt',
          },
        });

        // Ensure heading levels are preserved correctly
        // Verify heading conversions are working as expected
        const headingCommand = editor.commands.get('heading');
        if (headingCommand) {
          console.log('Heading command available');
        }

        // Intercept link command to add dofollow/nofollow functionality
        const linkCommand = editor.commands.get('link');
        const originalExecute = linkCommand.execute.bind(linkCommand);
        
        linkCommand.execute = function(url, attributes) {
          // Show dofollow/nofollow options (same as ReactQuill version)
          const followType = window.confirm('Click OK for dofollow link, Cancel for nofollow link');
          // followType = true means OK clicked = dofollow (no rel="nofollow")
          // followType = false means Cancel clicked = nofollow (add rel="nofollow")
          pendingNofollow = !followType;
          console.log('Link follow type:', followType ? 'dofollow' : 'nofollow', 'URL:', url);
          
          // Execute the original link command
          return originalExecute(url, attributes);
        };

        // Listen for document changes to apply alt text and nofollow links
        editor.model.document.on('change:data', () => {
          const model = editor.model;
          
          // Handle pending alt text for images
          if (pendingAltText !== null) {
            // Use setTimeout to ensure image is fully inserted
            setTimeout(() => {
              const root = model.document.getRoot();
              const differ = model.document.differ;
              const changes = differ.getChanges({ includeChangesInGraveyard: false });
              
              let imageElement = null;
              
              // First, try to find the most recently inserted image
              for (let i = changes.length - 1; i >= 0; i--) {
                const change = changes[i];
                if (change.type === 'insert' && change.name === 'imageBlock') {
                  imageElement = change.position.nodeAfter;
                  break;
                }
              }
              
              // If not found in changes, search all images for one without alt
              if (!imageElement) {
                const range = model.createRangeIn(root);
                for (const value of range.getWalker()) {
                  const item = value.item;
                  if (item.is('element', 'imageBlock')) {
                    const alt = item.getAttribute('alt');
                    if (!alt || alt === '') {
                      imageElement = item;
                      break;
                    }
                  }
                }
              }
              
              // Apply alt text to the found image
              if (imageElement) {
                model.change((writer) => {
                  writer.setAttribute('alt', pendingAltText || '', imageElement);
                  console.log('Applied alt text to image:', pendingAltText);
                });
              } else {
                console.warn('Could not find image element to apply alt text');
              }
              
              pendingAltText = null;
            }, 150);
          }
          
          // Handle pending nofollow for links
          if (pendingNofollow !== null) {
            setTimeout(() => {
              const model = editor.model;
              const root = model.document.getRoot();
              const selection = model.document.selection;
              const range = selection.getFirstRange();
              const differ = model.document.differ;
              const changes = differ.getChanges({ includeChangesInGraveyard: false });
              
              let linkElement = null;
              
              // First, use document differ to find recently inserted/modified link
              for (let i = changes.length - 1; i >= 0; i--) {
                const change = changes[i];
                
                // Check for link insertion
                if (change.type === 'insert' && change.name === 'link') {
                  linkElement = change.position.nodeAfter;
                  break;
                }
                
                // Check for text insertion inside a link
                if (change.type === 'insert' && change.name === '$text') {
                  let parent = change.position.parent;
                  while (parent) {
                    if (parent.is('element', 'link')) {
                      linkElement = parent;
                      break;
                    }
                    parent = parent.parent;
                  }
                  if (linkElement) break;
                }
                
                // Check for attribute changes on links
                if (change.type === 'attribute' && change.attributeKey === 'linkHref') {
                  const position = change.range.start;
                  let item = position.nodeAfter || position.parent;
                  while (item) {
                    if (item.is('element', 'link')) {
                      linkElement = item;
                      break;
                    }
                    item = item.parent;
                  }
                  if (linkElement) break;
                }
              }
              
              // If not found in changes, check current selection
              if (!linkElement && range) {
                // Check if selection is directly in a link
                let item = range.start.nodeAfter || range.start.parent;
                while (item) {
                  if (item.is('element', 'link')) {
                    linkElement = item;
                    break;
                  }
                  item = item.parent;
                }
                
                // If not found, walk through the range
                if (!linkElement) {
                  for (const value of range.getWalker()) {
                    const item = value.item;
                    
                    // Check if item is a link
                    if (item.is('element', 'link')) {
                      linkElement = item;
                      break;
                    }
                    
                    // Check parent chain for link
                    let parent = item.parent;
                    while (parent) {
                      if (parent.is('element', 'link')) {
                        linkElement = parent;
                        break;
                      }
                      parent = parent.parent;
                    }
                    
                    if (linkElement) break;
                  }
                }
              }
              
              // If still not found, search all links for the most recent one without rel
              if (!linkElement) {
                const docRange = model.createRangeIn(root);
                for (const value of docRange.getWalker()) {
                  const item = value.item;
                  if (item.is('element', 'link')) {
                    // Prefer links without rel attribute (newly created)
                    const currentRel = item.getAttribute('rel');
                    if (!currentRel || currentRel === '') {
                      linkElement = item;
                      break;
                    }
                  }
                }
                // If all links have rel, just use the first one found
                if (!linkElement) {
                  const docRange = model.createRangeIn(root);
                  for (const value of docRange.getWalker()) {
                    const item = value.item;
                    if (item.is('element', 'link')) {
                      linkElement = item;
                      break;
                    }
                  }
                }
              }
              
              // Apply or remove rel attribute based on user choice
              if (linkElement) {
                const shouldBeNofollow = pendingNofollow === true;
                const href = linkElement.getAttribute('href');
                console.log('Applying rel attribute to link. Should be nofollow:', shouldBeNofollow, 'URL:', href);
                
                model.change((writer) => {
                  if (shouldBeNofollow) {
                    // User chose nofollow (Cancel) - add rel="nofollow"
                    writer.setAttribute('rel', 'nofollow', linkElement);
                    console.log('Added rel="nofollow" to link:', href);
                  } else {
                    // User chose dofollow (OK) - ensure no rel attribute
                    const currentRel = linkElement.getAttribute('rel');
                    if (currentRel) {
                      writer.removeAttribute('rel', linkElement);
                      console.log('Removed rel attribute from link (dofollow):', href);
                    } else {
                      console.log('Link already has no rel attribute (dofollow):', href);
                    }
                  }
                });
              } else {
                console.warn('Could not find link element to apply rel attribute');
              }
              
              pendingNofollow = null;
            }, 150);
          }
        });
      }}
      config={{
        licenseKey: 'GPL',
        toolbar: [
          'heading',
          '|',
          'fontFamily',
          'fontSize',
          '|',
          'bold',
          'italic',
          'underline',
          'strikethrough',
          '|',
          'fontColor',
          'fontBackgroundColor',
          '|',
          'subscript',
          'superscript',
          '|',
          'bulletedList',
          'numberedList',
          '|',
          'indent',
          'outdent',
          '|',
          'alignment',
          '|',
          'blockQuote',
          'codeBlock',
          '|',
          'link',
          'imageUpload',
          'mediaEmbed',
          '|',
          'insertTable',
          '|',
          'undo',
          'redo',
        ],
        heading: {
          options: [
            { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
            { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
            { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
            { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
            { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
            { model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
            { model: 'heading6', view: 'h6', title: 'Heading 6', class: 'ck-heading_heading6' }
          ]
        },
        table: {
          contentToolbar: [
            'tableColumn',
            'tableRow',
            'mergeTableCells',
          ],
        },
      }}
      style={{
        height: "500px",
        width: "500px"
      }}
      onChange={(_, editor) => {
        let html = editor.getData();
        
        // Post-process HTML to ensure rel attributes are preserved
        // Parse HTML and add rel attributes to links that have them in the model
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const links = doc.querySelectorAll('a[href]');
        
        // Get all links from the model with their rel attributes
        const model = editor.model;
        const root = model.document.getRoot();
        const range = model.createRangeIn(root);
        const modelLinks = new Map();
        
        // Track all links and their rel status (null means dofollow, string means nofollow value)
        for (const value of range.getWalker()) {
          const item = value.item;
          if (item.is('element', 'link')) {
            const href = item.getAttribute('href');
            const rel = item.getAttribute('rel');
            // Store rel value (could be 'nofollow' or null/undefined for dofollow)
            modelLinks.set(href, rel || null);
          }
        }
        
        // Apply rel attributes to HTML links based on model state
        links.forEach((link) => {
          const href = link.getAttribute('href');
          const modelRel = modelLinks.get(href);
          
          if (modelRel) {
            // Link has rel attribute in model (nofollow)
            link.setAttribute('rel', modelRel);
          } else {
            // Link should be dofollow - explicitly remove rel attribute
            link.removeAttribute('rel');
          }
        });
        
        // Also ensure image alt attributes are preserved
        const images = doc.querySelectorAll('img');
        const modelImages = new Map();
        
        // Get all images from model with their alt attributes
        for (const value of range.getWalker()) {
          const item = value.item;
          if (item.is('element', 'imageBlock')) {
            const src = item.getAttribute('src');
            const alt = item.getAttribute('alt');
            if (src && alt) {
              modelImages.set(src, alt);
            }
          }
        }
        
        // Apply alt attributes to HTML images
        images.forEach((img) => {
          const src = img.getAttribute('src');
          const alt = modelImages.get(src);
          if (alt) {
            img.setAttribute('alt', alt);
          }
        });
        
        html = doc.body.innerHTML;
        onChange(html);
      }}
    />
  );
}
